import { Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DialogRef, DialogService } from '@progress/kendo-angular-dialog';
import { DocumentStatus } from '@/core/enums/document-status.enum';
import { DocumentDisplayModel } from '@/api/models/ace/xFound/Portal/Core/CAQ/Models/document-display-model';
import { AnfrageService } from '@/api/service-index';
import { LoadingAnimationService } from '@/core/services/loading-animation.service';
import { StateService } from '@/core/services/state.service';
import { PortalContextService } from '@/core/services/portal-context.service';
import { I18nService } from '@/core/services/i18nService';
import { ConfirmationDialogAction, ConfirmationDialogComponent, ConfirmationDialogResult } from '../../dialogs/confirmation-dialog/confirmation-dialog.component';
import { DocumentService } from '@/core/services/document.service';
import { Constants } from '@/constants';

@Component({
  selector: 'document-view-item',
  templateUrl: './document-view-item.component.html',
  styleUrls: ['./document-view-item.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DocumentViewItemComponent),
      multi: true
    }
  ]
})
export class DocumentViewItemComponent implements ControlValueAccessor {
  protected onChange: any = () => { };
  protected onTouched: any = () => { };
  protected _document: DocumentDisplayModel;
  protected _type: string;
  protected _documents: DocumentDisplayModel[];
  protected _originalDocuments: DocumentDisplayModel[];
  protected deleteProjektDialog: DialogRef;
  protected _disabledState: boolean;
  @Input() public paymentId: string;

  @Output('documentUpload') documentUploadEventEmitter: EventEmitter<DocumentDisplayModel> = new EventEmitter<DocumentDisplayModel>();
  @Output('documentDelete') documentDeleteEventEmitter: EventEmitter<DocumentDisplayModel> = new EventEmitter<DocumentDisplayModel>();

  @Input()
  public set documents(documents: DocumentDisplayModel[]) {
    this._documents = documents;
  }

  @Input()
  public set originalDocuments(documents: DocumentDisplayModel[]) {
    this._originalDocuments = documents;
  }

  @Input('disabled')
  public set disabled(value: boolean) {
    this._disabledState = value;
  }

  public get originalDocuments(): DocumentDisplayModel[] {
    return this._originalDocuments;
  }

  public get documents(): DocumentDisplayModel[] {
    return this._documents;
  }

  public get value(): DocumentDisplayModel {
    return this._document;
  }

  public set value(value: DocumentDisplayModel) {
    this._document = value;
    this.onChange(value);
    this.onTouched();
  }

  public get documentStatus(): DocumentStatus | undefined {
    if (!this.documents)
      return undefined;

    // ToDo: The document is allready there if uploaded another time with the same name. originalDocuments have to be refreshed on delete too.
    if (this.documents && this.documents.find(document => document.name == this._document.name))
      return DocumentStatus.Uploaded;

    return DocumentStatus.New;
  }

  public get isDisabled(): boolean {
    return this._disabledState;
  }

  constructor(protected stateService: StateService,
    protected loadingAnimationService: LoadingAnimationService,
    protected documentService: DocumentService,
    protected anfrageService: AnfrageService,
    protected dialogService: DialogService, 
    protected portalContextService: PortalContextService,
    protected languageservice: I18nService) { }

  public onValueChange(event: DocumentDisplayModel) {
    this.onChange(this.value);
    this.onTouched();
  }

  public get documentName(): string {
    return this._document && this._document.name ? this._document.name : "";
  }

  public get documentSize(): string {
    return this._document && this._document.fileSize ? ((this._document.fileSize / 1000000)).toFixed(2) + ' MB' : "";
  }

  public get isDocumentStatusUndefined(): boolean {
    return this.documentStatus == undefined;
  }

  public get isDocumentNew(): boolean {
    return this.documentStatus == DocumentStatus.New;
  }

  public get isDocumentUploaded(): boolean {
    return this.documentStatus == DocumentStatus.Uploaded;
  }

  public get isUploadButtonVisible(): boolean {
    return this._document && !this.isDocumentStatusUndefined && this.isDocumentNew && !this.isDisabled;
  }

  public get isDownloadButtonVisible(): boolean {
    return this._document && !this.isDocumentStatusUndefined && this.isDocumentUploaded;
  }

  public get isDeleteButtonVisible(): boolean {
    return this._document && !this.isDisabled && !this.isDocumentStatusUndefined && (this.isDocumentNew || this.isDocumentUploaded);
  }


  public onUploadDocumentClick() {
    this.uploadDocument();
  }

  public onDownloadDocumentClick() {
    this.downloadDocument();
  }

  public onDeleteDocumentClick() {
    this.openDeleteProjektDialog();
  }

  public uploadDocument() {
    if (this.portalContextService.CurrentAnfrage?.id) {
      this.anfrageService.apiAnfrageAnfrageIdAttachmentPatch({ anfrageId: this.portalContextService.CurrentAnfrage.id, body: this._document }).subscribe(response => {
        if (response)
          this.originalDocuments.push(response);
          
        this.documentUploadEventEmitter.emit(this._document);
      })
    }
  }

  public downloadDocument() {
    if (this._document.documentId) {
      if (this.portalContextService.CurrentAnfrage?.id != null) {
        let subscription = this.anfrageService.apiAnfrageAnfrageIdAttachmentDocumentIdGet({ anfrageId: this.portalContextService.CurrentAnfrage.id, documentId: this._document.documentId, loadContent: true, gutachtenId: this.portalContextService.CurrentGutachten?.gutachtenId ?? undefined  })
          .subscribe(file => {
            if (file)
              this.documentService.download(file)
          });

        this.loadingAnimationService.showSpinnerUsingSubscription(subscription);
      }
    }
  }

  public deleteDocument() {
    if (this.isDocumentUploaded)
      this.deleteDocumentFromServer();
    else {
      this.removeViewItem();
    }
  }

  public deleteDocumentFromServer() {
    if (this._document.documentId) {
      if (this.portalContextService.CurrentAnfrage?.id) {
        let subscription = this.anfrageService.apiAnfrageAnfrageIdAttachmentDocumentIdDelete({ documentId: this._document.documentId, anfrageId: this.portalContextService.CurrentAnfrage.id, projektberichtId: this.portalContextService.CurrentBericht?.projektberichtId, paymentId: this.paymentId, gutachtenId: this.portalContextService.CurrentGutachten?.gutachtenId ?? undefined})
          .subscribe(() => {
            this.documents = this.documents.filter(documentItem => documentItem.name != this._document.name);
            this.originalDocuments = this.originalDocuments.filter(documentItem => documentItem.name != this._document.name);
            this.documentDeleteEventEmitter.emit(this._document);

            this.anfrageService.apiAnfrageAnfrageIdValidateGet({ anfrageId: this.portalContextService.CurrentAnfrage.id, reportId: this.portalContextService.CurrentBericht?.projektberichtId, gutachtenId: this.portalContextService.CurrentGutachten?.gutachtenId ?? undefined }).subscribe(result => {
              this.stateService.ValidationErrors = result;
            });
          });

        this.loadingAnimationService.showSpinnerUsingSubscription(subscription);
      }
    }
  }

  public removeViewItem() {
    this.documents = this.documents.filter(documentItem => documentItem.name != this._document.name);
    this.documentDeleteEventEmitter.emit(this._document);
  }

  writeValue(value: any): void {
    if (value) {
      this.value = value;
    }

    if (value === null) {
      this._document = new DocumentDisplayModel();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  protected openDeleteProjektDialog() {
    this.deleteProjektDialog = this.dialogService.open({
      content: ConfirmationDialogComponent,
      maxWidth: Constants.DIALOG.MAX_WIDTH,
      maxHeight: Constants.DIALOG.MAX_HEIGHT,
      width: Constants.DIALOG.WIDTH,
      title: this.languageservice.instant("deletedocumentdialog.title")
    });

    let dialogComponent: ConfirmationDialogComponent = this.deleteProjektDialog.content.instance;

    dialogComponent.content = "deletedocumentdialog.infotext";
    dialogComponent.confirm = "document.delete";

    this.deleteProjektDialog.result.subscribe(result => this.onDeleteProjektDialogClose(<ConfirmationDialogResult>result));
  }

  protected onDeleteProjektDialogClose(result: ConfirmationDialogResult) {
    if (result.action == ConfirmationDialogAction.Confirm) {
      this.deleteDocument();
    }
  }
}
